import api from '@shared/services/api';
import { useAuthStore } from '@features/auth/store/authStore';
import {
  StudyPlanResponse,
  StudyPlanStreamEvent,
} from '../types/study-plan.types';

/**
 * Service for interacting with the Study Plan API
 */
export class StudyPlanService {
  /**
   * Get the study plan for the user
   * @param testProgressId Optional test progress ID to get a specific study plan
   * @returns Study plan response
   */
  static async getStudyPlan(testProgressId?: string): Promise<StudyPlanResponse> {
    // If testProgressId is provided, use the specific endpoint
    if (testProgressId) {
      const response = await api.get(`/study-plan/${testProgressId}`);
      return response.data;
    }
    
    // Otherwise, get the default study plan (diagnostic test)
    const response = await api.get('/study-plan');
    return response.data;
  }

  /**
   * Generate a study plan for a test progress
   * @param testProgressId Test progress ID
   * @returns Study plan ID
   */
  static async generateStudyPlan(testProgressId: string): Promise<string> {
    const response = await api.post(`/study-plan/${testProgressId}/generate`);
    return response.data.studyPlanId;
  }

  /**
   * Stream the study plan generation for a test progress
   * @param testProgressId Test progress ID
   * @param onEvent Callback for stream events
   */
  static streamStudyPlanGeneration(
    testProgressId: string,
    onEvent: (event: StudyPlanStreamEvent) => void
  ): () => void {
    // Get the current auth token
    const token = useAuthStore.getState().token;
    
    // Create EventSource for SSE with auth token
    const streamUrl = new URL(`${api.defaults.baseURL}/study-plan/${testProgressId}/stream`);
    
    // Add token as query parameter for authentication
    if (token) {
      streamUrl.searchParams.append('token', token);
    }
    
    // Create EventSource with the URL including the token
    const eventSource = new EventSource(streamUrl.toString());

    // Set up event handlers
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onEvent(data);

        // Close the connection when the generation is complete or fails
        if (data.type === 'complete' || data.type === 'error') {
          eventSource.close();
        }
      } catch (error) {
        console.error('Error parsing SSE event:', error);
      }
    };

    eventSource.onerror = () => {
      console.error('SSE connection error');
      eventSource.close();
      onEvent({
        type: 'error',
        error: 'Connection to the server was lost',
      });
    };

    // Return a function to close the connection
    return () => {
      eventSource.close();
    };
  }
}
